class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 100);

    this.filtersForm = this.querySelector('form');

    this.filtersForm?.addEventListener('input', this.debouncedOnSubmit.bind(this));

    this.filtersForm?.querySelectorAll('.button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
      });
    });

    function debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : CollectionFilters.searchParamsInitial;
      if (searchParams === CollectionFilters.searchParamsPrev) return;
      CollectionFilters.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /**
   * @param {Boolean} hasActiveFilters
   * */
  static toggleHasActiveFilters(hasActiveFilters) {
    const body = document.body;
    const activeFiltersClass = 'has-active-filters';
    const indexLayout = document.querySelector('[js-filter-index-layout]');

    if (hasActiveFilters) {
      body.classList.add(activeFiltersClass);
      indexLayout?.setAttribute('disabled', 'true');
    } else {
      body.classList.remove(activeFiltersClass);
      indexLayout?.removeAttribute('disabled');
    }
  }

  /**
   * @param {String} searchParams
   * @param {Event} event
   * @param {Boolean} updateURLHash
   * */
  static renderPage(searchParams, event, updateURLHash = true) {
    CollectionFilters.searchParamsPrev = searchParams;
    const sections = CollectionFilters.getSections();
    const productList = document.querySelector('[js-filter-product-list]');
    const productListLoadingClass = 'product-list--is-loading';
    productList?.classList.add(productListLoadingClass);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      CollectionFilters.filterData.some(filterDataUrl)
        ? CollectionFilters.renderSectionFromCache(filterDataUrl, event)
        : CollectionFilters.renderSectionFromFetch(url, event);
    });

    productList?.classList.remove(productListLoadingClass);

    if (updateURLHash) CollectionFilters.updateURLHash(searchParams);
  }

  /**
   * @param {String} url
   * @param {Event} event
   * */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        CollectionFilters.filterData = [...CollectionFilters.filterData, { html, url }];
        CollectionFilters.renderFilters(html, event);
        CollectionFilters.renderProductListContainer(html);
        CollectionFilters.renderProductsCount(html);
        CollectionFilters.renderActiveFiltersCount(html);
        CollectionFilters.updateLoadMoreTrigger(html);
      });
  }

  /**
   * @param {String} filterDataUrl
   * @param {Event} event
   * */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = CollectionFilters.filterData.find(filterDataUrl).html;
    CollectionFilters.renderFilters(html, event);
    CollectionFilters.renderProductListContainer(html);
    CollectionFilters.renderProductsCount(html);
    CollectionFilters.renderActiveFiltersCount(html);
    CollectionFilters.updateLoadMoreTrigger(html);
  }

  /**
   * @param {String} html
   * */
  static renderProductListContainer(html) {
    if (!document.querySelector('[js-filter-product-list]')) return;
    document.querySelector('[js-filter-product-list]').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[js-filter-product-list]').innerHTML;
  }

  /**
   * @param {String} html
   * */
  static renderProductsCount(html) {
    document.querySelectorAll('[js-filter-products-count]').forEach((productsCount) => {
      productsCount.innerHTML = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[js-filter-products-count]').innerHTML;
    });
  }

  /**
   * @param {String} html
   * */
  static renderActiveFiltersCount(html) {
    const activeFiltersCount = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[js-filter-active-filters-count]').innerHTML;

    document.querySelectorAll('[js-filter-active-filters-count]').forEach((currentActiveFiltersCount) => {
      currentActiveFiltersCount.innerHTML = activeFiltersCount;
    });

    if (parseInt(activeFiltersCount) > 0) {
      CollectionFilters.toggleHasActiveFilters(true);
    } else {
      CollectionFilters.toggleHasActiveFilters(false);
    }
  }

  /**
   * @param {string} html
   * @param {Event} [event]
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    /** @type {NodeListOf<HTMLElement>} */
    const facetDetailsElements = parsedHTML.querySelectorAll('.collection-filters .js-filter-collection');

    /**
     * @param {HTMLElement} el
     * @returns {boolean}
     */
    const matchesIndex = (el) => {
      const origin =
        event?.target instanceof Element
          ? /** @type {HTMLElement|null} */ (event.target.closest('.js-filter-collection'))
          : null;
      const a = el.dataset?.index ?? null;
      const b = origin?.dataset?.index ?? null;
      return !!(a && b && a === b);
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((el) => !matchesIndex(el));

    facetsToRender.forEach((el) => {
      const idx = el.dataset?.index;
      if (!idx) return;
      const target = /** @type {HTMLElement|null} */ (
        document.querySelector(`.js-filter-collection[data-index="${idx}"]`)
      );
      if (!target) return;

      target.innerHTML = el.innerHTML;
      const container = target.closest('.js-filter-collection-container');
      const newContainer = parsedHTML
        .querySelector(`.js-filter-collection[data-index="${idx}"]`)
        ?.closest('.js-filter-collection-container');

      if (container instanceof HTMLElement) {
        container.style.display = '';

        const usableEl = container.querySelector('.js-filter-collection-usable-filters');
        const newUsableEl = newContainer?.querySelector('.js-filter-collection-usable-filters');

        if (usableEl) {
          usableEl.textContent = newUsableEl ? newUsableEl.textContent : '0';
        }
      }
    });

    /** @type {NodeListOf<HTMLElement>} */
    const currentFilters = document.querySelectorAll('.collection-filters .js-filter-collection');

    currentFilters.forEach((el) => {
      const idx = el.dataset?.index;
      if (!idx) return;
      const stillExists = Array.from(facetDetailsElements).some((neu) => neu.dataset?.index === idx);
      const container = el.closest('.js-filter-collection-container');
      if (container instanceof HTMLElement) {
        container.style.display = stillExists ? '' : 'none';
      }
    });
  }

  /**
   * @param {String} html
   * */
  static updateLoadMoreTrigger(html) {
    /**
     * @type HTMLElement | null
     * */
    const fetchedLoadMoreProducts = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('load-more-products');
    /**
     * @type HTMLElement | null
     * */
    const loadMoreProducts = document.querySelector('load-more-products');

    if (!loadMoreProducts || !fetchedLoadMoreProducts) return;

    /**
     * @type String | undefined
     * */
    const url = fetchedLoadMoreProducts.dataset.url;

    url ? loadMoreProducts.setAttribute('data-url', url) : loadMoreProducts?.removeAttribute('data-url');
  }

  /**
   * @param {String} searchParams
   * */
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.querySelector('[js-filter-product-list]').dataset.sectionId,
      },
    ];
  }

  /**
   * @param {HTMLFormElement} form
   * */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /**
   * @param {String} searchParams
   * @param {Event} event
   * */
  onSubmitForm(searchParams, event) {
    CollectionFilters.renderPage(searchParams, event);
  }

  /**
   * @param {Event} event
   * */
  onSubmitHandler(event) {
    event.preventDefault();
    const form = document.querySelector('collection-filters form');

    this.onSubmitForm(this.createSearchParams(form), event);
  }

  /**
   * @param {Event} event
   * */
  onActiveFilterClick(event) {
    const url =
      event.currentTarget.dataset.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.dataset.href.slice(event.currentTarget.dataset.href.indexOf('?') + 1);
    CollectionFilters.renderPage(url);
  }
}

class ClearFilter extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.closeFilter.bind(this));
  }

  /**
   * @param {Event} event
   * */
  closeFilter(event) {
    event.preventDefault();
    const form = document.querySelector('collection-filters');
    form.onActiveFilterClick(event);
  }
}

/**
 * @type {Array<HTMLElement>}
 * */
CollectionFilters.filterData = [];
CollectionFilters.searchParamsInitial = window.location.search.slice(1);
CollectionFilters.searchParamsPrev = window.location.search.slice(1);
customElements.define('collection-filters', CollectionFilters);
CollectionFilters.setListeners();

customElements.define('clear-filter', ClearFilter);
